import { Layout } from '@/components/Layout';
import { useSEO } from '@/lib/useSEO';
import { ShieldCheck, CheckCircle2, XCircle, FileText, Clock, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Página pública dedicada a garantías.
 * Reemplaza el ancla `/ayuda#garantia` con una página independiente que
 * cubre condiciones, cobertura, exclusiones y proceso — como pidió el
 * cliente para el Deploy inicial de AutoMarket Colombia.
 */
const COVERED = [
  'Defectos de fabricación del repuesto o accesorio.',
  'Fallas de funcionamiento no derivadas de instalación incorrecta.',
  'Piezas OEM originales con vicios ocultos de fábrica.',
  'Producto entregado que no coincide con la descripción publicada.',
];

const EXCLUDED = [
  'Daño por instalación incorrecta o realizada por personal no técnico.',
  'Uso indebido, sobreesfuerzo o modificación del repuesto.',
  'Desgaste normal por uso o kilometraje.',
  'Productos sin embalaje original o con el sello de garantía roto.',
  'Repuestos usados o remanufacturados vendidos como tal (salvo pacto expreso).',
];

const PROCESS = [
  {
    title: '1) Radica la solicitud',
    body:
      'Desde “Mis pedidos” abre el detalle del producto y selecciona “Solicitar garantía”, o radica una PQR indicando el número de pedido. Incluye fotos y una descripción clara de la falla, además de la factura o comprobante de compra.',
  },
  {
    title: '2) Revisión',
    body:
      'AutoMarket recibe la solicitud, la asigna a un gestor y la traslada al vendedor y/o fabricante para su evaluación conforme a las condiciones de garantía informadas en el producto y a la garantía legal vigente. Los tiempos y la decisión sobre la garantía dependen del vendedor y/o fabricante.',
  },
  {
    title: '3) Solución acordada con el vendedor / fabricante',
    body:
      'Cuando la garantía procede, el vendedor y/o fabricante define, según la ley y sus propias condiciones, la solución aplicable (por ejemplo, reparación, reemplazo o reintegro). AutoMarket no asume directamente costos de recolección, reparación, cambio ni reembolso que correspondan al vendedor y/o fabricante; su rol es de gestión, seguimiento y acompañamiento.',
  },
  {
    title: '4) Cierre y seguimiento',
    body:
      'Una vez ejecutada la solución acordada, AutoMarket cierra el caso en la plataforma. El comprador puede calificar la experiencia y la gestión queda registrada en el historial del vendedor.',
  },
];

export default function Garantias() {
  useSEO({
    title: 'Garantías | AutoMarket Colombia',
    description:
      'Condiciones, cobertura, exclusiones y proceso para solicitar garantía en AutoMarket Colombia. Compra con la tranquilidad de tener respaldo.',
    canonical: '/garantias',
  });

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center bg-orange-500 text-slate-900">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">Respaldo</div>
            <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="garantias-title">
              Garantías
            </h1>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 space-y-10">
        <section className="border-l-2 border-orange-500 pl-5" data-testid="garantias-cobertura">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-slate-900" />
            <h2 className="font-display text-xl font-black tracking-tight text-slate-900">Condiciones generales</h2>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            En AutoMarket Colombia aplica la <strong>garantía legal</strong> correspondiente conforme al Estatuto del Consumidor (Ley 1480 de 2011) y, cuando exista, la <strong>garantía adicional</strong> ofrecida por el vendedor y/o fabricante. Cada producto muestra el término y las condiciones de garantía informados por el vendedor / fabricante en la publicación. El comprador conserva la factura o comprobante de compra como prueba del período de cobertura.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            AutoMarket actúa como plataforma de gestión, seguimiento y acompañamiento del proceso de garantía. Las obligaciones de reparación, cambio, reintegro y las que correspondan por la garantía del producto son responsabilidad del vendedor y/o fabricante, según la ley y las condiciones informadas en cada publicación.
          </p>
        </section>

        <section className="border-l-2 border-emerald-600 pl-5" data-testid="garantias-cubierto">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <h2 className="font-display text-xl font-black tracking-tight text-slate-900">Qué cubre</h2>
          </div>
          <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
            {COVERED.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </section>

        <section className="border-l-2 border-red-600 pl-5" data-testid="garantias-excluido">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <h2 className="font-display text-xl font-black tracking-tight text-slate-900">Qué NO cubre</h2>
          </div>
          <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
            {EXCLUDED.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </section>

        <section className="border-l-2 border-slate-500 pl-5" data-testid="garantias-proceso">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-900" />
            <h2 className="font-display text-xl font-black tracking-tight text-slate-900">Cómo solicitar la garantía</h2>
          </div>
          <div className="mt-4 space-y-4">
            {PROCESS.map((s) => (
              <div key={s.title}>
                <div className="text-sm font-semibold text-slate-900">{s.title}</div>
                <p className="text-sm text-slate-700 mt-1">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="border border-slate-200 bg-slate-50 p-6">
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <HelpCircle className="h-5 w-5" /> ¿Necesitas radicar una garantía?
          </div>
          <p className="mt-2 text-sm text-slate-600">
            El canal oficial es la PQR con el número del pedido. Nuestro equipo la asigna a un gestor y le da seguimiento hasta el cierre.
          </p>
          <Link
            to="/pqr"
            data-testid="garantias-cta-pqr"
            className="mt-4 inline-flex items-center gap-2 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Radicar PQR de garantía
          </Link>
        </div>
      </div>
    </Layout>
  );
}
